import { useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { LazyQueryExecFunction, QueryResult } from '@apollo/client';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  GridFilterModel,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  Estate,
  Estate_EstateListDataGridPageFilter,
  Estate_FieldsFragment,
  EstateListDataGridColumns,
  FeatureSubscriptionType,
  PortalPermissions,
  useEstate_UpdateEstateMutation,
} from 'queries';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import EstateListActionsMenu from './estate-list-actions-menu';
import EstatePointPerson from './estate-point-person';
import EstateTypeChip from './estate-type-chip';

const useStyles = makeStyles(() => ({
  container: {
    color: '#1C2541',
  },
  headerItem: {
    display: 'flex',
    alignItems: 'center',
  },
  headerIcon: {
    marginRight: '8px',
  },
  buttonContainer: {
    display: 'flex',
    gap: '20px',
  },
  button: {
    backgroundColor: '#FFF',
    color: '#1C2541',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
    border: '2px solid #1C2541',
  },
  buttonPrimary: {
    backgroundColor: '#5ED1F0',
    color: '#1C2541',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '12px',
    px: '16px',
    width: '120px',
    border: '2px solid #5ED1F0',
  },
  dataGrid: {
    border: 0,
    color: '#1C2541',
    borderRadius: '8px',
    '& .MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
      borderRadius: '8px',
      borderBottom: 'none',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      borderTop: 'none',
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  pageHeader: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '32px',
  },
}));

type Props = {
  featureSubscriptionType: FeatureSubscriptionType;
  setShowArchivedEstates: Function;
  lazyQueryResult: QueryResult<any, any>;
  archivedLazyQueryResult: QueryResult<any, any>;
  executeArchivedLazyQuery: LazyQueryExecFunction<any, any>;
};

const ArchivedEstateList = ({
  featureSubscriptionType,
  setShowArchivedEstates,
  lazyQueryResult,
  archivedLazyQueryResult,
  executeArchivedLazyQuery,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();

  const gridApiRef = useGridApiRef();

  const baseFilterModelFromDataGridTab: GridFilterModel = {
    items: [
      {
        field: EstateListDataGridColumns.IsArchived,
        operator: 'equals',
        value: 'true',
      },
    ],
  };

  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const [updateEstate] = useEstate_UpdateEstateMutation({
    onCompleted: () => {},
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const memoizedColumns = useMemo(() => {
    const handleAssignedUserChange = (estateId: string, userId: string | null) => {
      updateEstate({
        variables: {
          input: {
            id: estateId,
            portalId: portalId,
            assignedUserId: userId,
          },
        },
      });
    };

    const columns: GridColDef<Estate_FieldsFragment>[] = [];

    columns.push({
      flex: 2,
      field: EstateListDataGridColumns.EstateName,
      headerName: 'Estate Name',
      filterable: true,
      disableColumnMenu: true,
      renderHeader: () => (
        <Typography className={classes.headerItem}>
          <WorkOutlineIcon className={classes.headerIcon} fontSize="small" /> Client Matter
        </Typography>
      ),
      renderCell: ({ row }) => {
        return <div>{row.estateName}</div>;
      },
    });

    columns.push({
      minWidth: 200,
      field: EstateListDataGridColumns.Type,
      headerName: 'Type',
      filterable: true,
      disableColumnMenu: true,
      renderHeader: () => (
        <Typography className={classes.headerItem}>
          <WidgetsIcon className={classes.headerIcon} fontSize="small" /> Type
        </Typography>
      ),
      renderCell: ({ row }) => {
        return (
          <div>
            <EstateTypeChip estateType={row.type} />
          </div>
        );
      },
    });

    if (hasPortalPermissionAdmin || hasPortalPermissionUser) {
      columns.push({
        minWidth: 250,
        field: EstateListDataGridColumns.AssignedUser,
        headerName: 'Point Person',
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Typography className={classes.headerItem}>
            <PersonPinOutlinedIcon className={classes.headerIcon} fontSize="small" /> Point Person
          </Typography>
        ),
        renderCell: ({ row }) => {
          const estate = row as Estate;
          return (
            <EstatePointPerson
              userId={row.assignedUserId ?? undefined}
              estateId={estate.id}
              portalId={portalId}
              featureSubscriptionType={featureSubscriptionType}
              onChange={handleAssignedUserChange}
            />
          );
        },
      });
    }

    columns.push({
      minWidth: 400,
      field: 'actions',
      renderHeader: () => '',
      renderCell: ({ row }) => {
        const estate = row as Estate;
        return (
          <div className={classes.buttonContainer}>
            <Button
              color="primary"
              onClick={() =>
                history.push(
                  `/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.BALANCE_SHEET}`,
                )
              }
              className={classes.container}
            >
              Balance Sheet
            </Button>
            <Button
              component="span"
              className={classes.button}
              color="primary"
              variant="contained"
              size="small"
              onClick={() =>
                history.push(
                  `/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.VAULT}`,
                )
              }
            >
              Open Vault
            </Button>
            <Button
              component="span"
              className={classes.buttonPrimary}
              color="primary"
              variant="contained"
              size="small"
              onClick={() =>
                history.push(
                  `/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
                )
              }
            >
              View Dashboard
            </Button>
          </div>
        );
      },
    });

    columns.push({
      minWidth: 50,
      field: 'archive',
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => {
        const estate = params.row as Estate;
        return (
          <EstateListActionsMenu
            estate={estate}
            refetchEstates={() => {
              lazyQueryResult.refetch();
              archivedLazyQueryResult.refetch();
            }}
          />
        );
      },
    });

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classes,
    portalId,
    hasPortalPermissionAdmin,
    hasPortalPermissionUser,
    updateEstate,
    lazyQueryResult,
    archivedLazyQueryResult,
    featureSubscriptionType,
    history,
  ]);

  if (archivedLazyQueryResult?.data?.data?.edges.length === 0) {
    setShowArchivedEstates(false);
  }

  const getArchivedEstateListGridToolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  return (
    <>
      <Typography className={classes.pageHeader}>My Archived Estates</Typography>
      <Box className={classes.container}>
        <ServerEnabledDataGrid<Estate_EstateListDataGridPageFilter>
          className={classes.dataGrid}
          loading={archivedLazyQueryResult.loading}
          apiRef={gridApiRef}
          columns={memoizedColumns}
          gridStateKey={GridStateKey.ArchivedEstateList}
          executeLazyQuery={executeArchivedLazyQuery}
          queryResult={archivedLazyQueryResult}
          baseFilterModelFromDataGridTab={baseFilterModelFromDataGridTab}
          disableColumnReorder
          disableRowSelectionOnClick
          disableColumnMenu
          columnHeaderHeight={45}
          autoHeight={true}
          pagination
          pageSizeOptions={[5, 25, 50, 100]}
          slots={{
            toolbar: getArchivedEstateListGridToolbar,
          }}
        />
      </Box>
    </>
  );
};

export default ArchivedEstateList;
